/**
 * Credit Interceptor
 *
 * Axios response interceptor that monitors credit-consuming API responses.
 * - Emits EMITTER_CREDIT_UPDATE to signal Redux updates (handled by a component)
 * - Emits EMITTER_CREDIT_WARNING when backend returns status codes 301-304
 * - Emits EMITTER_LOW_CREDITS when available credits drop below threshold
 */

import EventEmitter from "../emitter/EventEmitter";
import {
  ALL_CREDIT_STATUS_CODES,
  CREDIT_THRESHOLD,
  STATUS_CODE_TO_MODAL,
  CREDIT_MODAL_TYPES,
  EMITTER_CREDIT_WARNING,
  EMITTER_LOW_CREDITS,
  EMITTER_CREDIT_UPDATE,
  EMITTER_SDR_STATUS,
} from "./creditStatusCodes";

/**
 * Process an API response for credit-related information.
 * Call this after any of the 5 monitored API calls.
 *
 * @param {Object} data - The response data body
 * @param {Object} config - The axios request config
 */
export const handleCreditResponse = (data, config = {}) => {
  if (!data) return false;

  // Suppression logic: Skip modals if explicitly requested in config
  // (e.g., during critical "opening" flows where the UI handles gating manually)
  if (config?.suppressModal) {
    return false;
  }

  // SDR plans have unlimited credits - notify Redux and skip all credit warnings
  const isSdr =
    data?.isSdr === true ||
    data?.isSdr === "true" ||
    data?.data?.isSdr === true ||
    data?.data?.isSdr === "true";
  if (isSdr) {
    EventEmitter.emit(EMITTER_SDR_STATUS, true);
    return false;
  }

  // 1. Check for credit failure status codes (301, 302, 303, 304)
  // Backend might return statusCode or status_code. Support both.
  // Also parse as int to handle potential string responses.
  const rawStatusCode =
    data?.statusCode ||
    data?.status_code ||
    data?.data?.statusCode ||
    data?.data?.status_code;
  const statusCode = rawStatusCode ? parseInt(rawStatusCode, 10) : null;

  if (statusCode && ALL_CREDIT_STATUS_CODES.includes(statusCode)) {
    const modalType = STATUS_CODE_TO_MODAL[statusCode];
    if (modalType) {
      EventEmitter.emit(EMITTER_CREDIT_WARNING, {
        modalType,
        message: data.message || data?.data?.message || "Credit issue detected",
        statusCode: statusCode,
      });
    }
    return true; // Return true to indicate we've handled a credit failure
  }

  // Message-based fallback: some endpoints return { status: false, message: "...credits..." }
  // without a numeric statusCode. Detect "not enough credits" wording and surface the
  // ZeroCreditsModal instead of a plain error toast.
  const apiStatus = data?.status ?? data?.data?.status;
  const apiMessage = (data?.message || data?.data?.message || "").toLowerCase();
  const looksLikeCreditShortage =
    apiStatus === false &&
    (apiMessage.includes("enough credits") ||
      apiMessage.includes("no credits") ||
      apiMessage.includes("insufficient credits"));

  if (looksLikeCreditShortage) {
    EventEmitter.emit(EMITTER_CREDIT_WARNING, {
      modalType: CREDIT_MODAL_TYPES.ZERO_CREDITS,
      message: data?.message || data?.data?.message,
    });
    return true;
  }

  // const rawCredits = data?.available_credits ?? data?.data?.available_credits;
  // const rawCredits =
  //   data?.available_credits ??
  //   data?.data?.available_credits ??
  //   data?.remaining_credits ??
  //   data?.data?.remaining_credits;
  const rawCredits = data?.remaining_credits ?? data?.data?.remaining_credits;
  if (rawCredits !== undefined && rawCredits !== null) {
    const credits = parseInt(rawCredits, 10);

    if (!isNaN(credits)) {
      EventEmitter.emit(EMITTER_CREDIT_UPDATE, credits);

      // 3. Check if credits are running low (skip if suppressModal is true)
      if (!config?.suppressModal && credits < CREDIT_THRESHOLD && credits > 0) {
        EventEmitter.emit(EMITTER_LOW_CREDITS, {
          modalType: CREDIT_MODAL_TYPES.LOW_CREDITS,
          message: `You have ${credits} credits remaining.`,
          availableCredits: credits,
        });
      }
    }
  }
};
